import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  Gauge,
  RefreshCw,
  AlertCircle,
  TrendingUp,
  Calendar,
  Clock,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Badge, Button } from '../../components/ui';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';

interface UserLimit {
  operationType: string;
  dailyLimit: number | null;
  perTransactionLimit: number | null;
  monthlyLimit: number | null;
  dailyConsumed: number;
  monthlyConsumed: number;
  dailyRemaining: number;
  monthlyRemaining: number;
  source: 'PLATFORM' | 'TENANT' | 'USER';
}

export function UserLimits() {
  const { data: limits, isLoading, error, refetch } = useQuery({
    queryKey: ['user-limits'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.limits.myLimits);
      return response.data as UserLimit[];
    },
  });

  const formatCurrency = (value: number | null) => {
    if (value === null) return 'Sem limite';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const calculatePercentage = (consumed: number, limit: number | null) => {
    if (limit === null || limit === 0) return 0;
    return Math.min((consumed / limit) * 100, 100);
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const operationLabels: Record<string, string> = {
    PIX_SEND: 'Pix Enviar',
    PIX_RECEIVE: 'Pix Receber',
    TRANSFER: 'Transferência',
    BOLETO: 'Boleto',
    WITHDRAWAL: 'Saque',
  };

  const sourceLabels: Record<string, string> = {
    PLATFORM: 'Plataforma',
    TENANT: 'Empresa',
    USER: 'Personalizado',
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <AlertCircle className="w-16 h-16 text-red-400" />
        <h2 className="text-xl font-semibold text-surface-100">Erro ao carregar limites</h2>
        <Button variant="primary" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Meus Limites</h1>
          <p className="text-surface-400">Visualize seus limites e consumo atual</p>
        </div>
        <Button variant="secondary" onClick={() => refetch()} disabled={isLoading}>
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Limits Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="animate-pulse bg-surface-800 rounded-xl h-64" />
          ))}
        </div>
      ) : !limits || limits.length === 0 ? (
        <Card>
          <CardBody>
            <div className="text-center py-12 text-surface-400">
              <Gauge className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum limite configurado</p>
            </div>
          </CardBody>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {limits.map(limit => {
            const dailyPercentage = calculatePercentage(limit.dailyConsumed, limit.dailyLimit);
            const monthlyPercentage = calculatePercentage(limit.monthlyConsumed, limit.monthlyLimit);

            return (
              <Card key={limit.operationType}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Gauge className="w-5 h-5 text-primary-400" />
                      {operationLabels[limit.operationType] || limit.operationType}
                    </span>
                    <Badge variant="info">{sourceLabels[limit.source]}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardBody className="space-y-6">
                  {/* Per Transaction Limit */}
                  <div className="flex items-center justify-between p-3 bg-surface-800/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-surface-400" />
                      <span className="text-surface-300">Por Transação</span>
                    </div>
                    <span className="text-surface-100 font-medium">
                      {formatCurrency(limit.perTransactionLimit)}
                    </span>
                  </div>

                  {/* Daily Limit */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-surface-400" />
                        <span className="text-surface-300">Limite Diário</span>
                      </div>
                      <span className="text-surface-100">
                        {formatCurrency(limit.dailyConsumed)} / {formatCurrency(limit.dailyLimit)}
                      </span>
                    </div>
                    <div className="h-2 bg-surface-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${getProgressColor(dailyPercentage)} transition-all duration-500`}
                        style={{ width: `${dailyPercentage}%` }}
                      />
                    </div>
                    <div className="flex justify-between mt-1 text-xs text-surface-500">
                      <span>Consumido: {dailyPercentage.toFixed(1)}%</span>
                      <span>Restante: {formatCurrency(limit.dailyRemaining)}</span>
                    </div>
                  </div>

                  {/* Monthly Limit */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-surface-400" />
                        <span className="text-surface-300">Limite Mensal</span>
                      </div>
                      <span className="text-surface-100">
                        {formatCurrency(limit.monthlyConsumed)} / {formatCurrency(limit.monthlyLimit)}
                      </span>
                    </div>
                    <div className="h-2 bg-surface-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${getProgressColor(monthlyPercentage)} transition-all duration-500`}
                        style={{ width: `${monthlyPercentage}%` }}
                      />
                    </div>
                    <div className="flex justify-between mt-1 text-xs text-surface-500">
                      <span>Consumido: {monthlyPercentage.toFixed(1)}%</span>
                      <span>Restante: {formatCurrency(limit.monthlyRemaining)}</span>
                    </div>
                  </div>

                  {/* Warning if near limit */}
                  {(dailyPercentage >= 80 || monthlyPercentage >= 80) && (
                    <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                      <p className="text-sm text-yellow-400">
                        ⚠️ Você está próximo do limite. Operações acima do limite precisarão de aprovação.
                      </p>
                    </div>
                  )}
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}

      {/* Info Card */}
      <Card>
        <CardBody>
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <h3 className="font-medium text-surface-100 mb-1">Sobre os Limites</h3>
              <p className="text-sm text-surface-400">
                Os limites são definidos em cascata: Plataforma → Empresa → Usuário. 
                Quando você excede um limite, a operação é enviada para aprovação do gerente.
                Os limites diários são resetados à meia-noite e os mensais no primeiro dia de cada mês.
              </p>
            </div>
          </div>
        </CardBody>
      </Card>
    </motion.div>
  );
}
